import { useEffect, useRef, useState } from "react";

import { useLanguage } from "../i18n/LanguageContext";
import "./IntroSplash.css";

/**
 * The opening: on a dark screen the handwritten mark writes itself in, the
 * subtitle appears, then the mark flies into its place in the nav corner
 * while the screen dissolves. Once per page load; a click skips it.
 */

const WRITE_MS = 2100;
const HOLD_MS = 900;
const FLY_MS = 1150;

// Plays once per page load (like a splash screen), not again when the
// visitor comes back to the home page from the menu within the site.
let playedThisLoad = false;

export function shouldShowIntro(): boolean {
  if (playedThisLoad) return false;
  if (typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return false;
  return true;
}

function markSeen() {
  playedThisLoad = true;
}

type Phase = "write" | "hold" | "fly" | "done";

export default function IntroSplash({ onDone }: { onDone: () => void }) {
  const { t } = useLanguage();
  const [phase, setPhase] = useState<Phase>("write");
  const markRef = useRef<HTMLImageElement>(null);
  const [fly, setFly] = useState<string | null>(null);
  const finished = useRef(false);

  const finish = () => {
    if (finished.current) return;
    finished.current = true;
    markSeen();
    document.body.classList.remove("intro-active");
    onDone();
  };

  // Lock the page while it plays and hide the real nav mark until we land on it.
  useEffect(() => {
    document.body.classList.add("intro-active");
    return () => document.body.classList.remove("intro-active");
  }, []);

  useEffect(() => {
    const a = window.setTimeout(() => setPhase("hold"), WRITE_MS);
    const b = window.setTimeout(() => setPhase("fly"), WRITE_MS + HOLD_MS);
    return () => {
      window.clearTimeout(a);
      window.clearTimeout(b);
    };
  }, []);

  // FLIP: measure where the nav's own mark sits and move ours onto it.
  useEffect(() => {
    if (phase !== "fly") return;
    const from = markRef.current?.getBoundingClientRect();
    const target = document.querySelector<HTMLElement>(".nav-brand img")?.getBoundingClientRect();
    if (from && target && target.width > 0) {
      const scale = target.height / from.height;
      const dx = target.left - from.left;
      const dy = target.top - from.top;
      setFly(`translate(${dx}px, ${dy}px) scale(${scale})`);
    } else {
      setFly("translate(0, -40vh) scale(0.3)");
    }
    const id = window.setTimeout(finish, FLY_MS + 150);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  return (
    <div className={`intro intro-${phase}`} onClick={finish} role="presentation" aria-hidden="true">
      <div className="intro-bg" />
      <div className="intro-stage">
        <img
          ref={markRef}
          className="intro-mark"
          src="/brand/wordmark-light.png"
          alt=""
          draggable={false}
          style={fly ? { transform: fly } : undefined}
        />
        <p className="intro-sub">
          {t("brandSub")} · {t("brandLine")}
        </p>
      </div>
      <span className="intro-skip">{t("close")}</span>
    </div>
  );
}
